import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Checkbox, Dropdown, Empty, Flex, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getSubmittedCandidates } from '../../../../services/dropdownApi';
import CustomPagination from '../../../../components/CustomPagination';
import unorderedListOutlinedIcon from '../../../../components/images/common/unorderedlistoutlined.svg';

const SUBMITTED_ACTIONS = [
  { key: 'mail', label: 'Send Email', route: '/popup/mail' },
];

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 50;
const MIN_VISIBLE = 6;

function statusColor(text = '') {
  const t = text.toLowerCase();
  if (['approved', 'selected', 'hired', 'offer accepted'].some(k => t.includes(k))) return 'success';
  if (['rejected', 'declined', 'not selected', 'rejected by'].some(k => t.includes(k))) return 'error';
  if (['evaluation', 'pending', 'waiting', 'review'].some(k => t.includes(k))) return 'warning';
  return 'default';
}

function payRateCell(r) {
  const rate = r.candidateRate?.candidateAcceptedRate;
  if (!rate) return '—';
  const symbol = r.candidateRate?.candidateAcceptedCurrency === 'USD' ? '$' : '';
  const unit   = r.candidateRate?.candidateAcceptedPayUnit || 'hr';
  return `${symbol}${rate} / ${unit}`;
}

function jobCell(r) {
  const title = r.jobTitle || '';
  const ref   = r.jobClientRefrenceId || r.jobRefrenceId || '';
  if (!title && !ref) return '—';
  return ref ? `${title} / ${ref}` : title;
}

function nameValue(r) {
  return `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—';
}

const ALL_COLUMNS = [
  {
    key: 'info',
    label: 'Info',
    width: 40,
    render: (_, r) => (
      <Tooltip title={`Sub ID: ${r.submissionId || '—'}`}>
        <InfoCircleOutlined style={{ color: 'var(--color-brand, #1677ff)', cursor: 'pointer' }} />
      </Tooltip>
    ),
  },
  {
    key: 'submissionId',
    label: 'Sub ID',
    dataIndex: 'submissionId',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          {v ? <span className="job-cell-link job-cell-value">{v}</span> : <Text className="job-cell-primary job-cell-value">—</Text>}
        </span>
      </span>
    ),
    sorter: (a, b) => (a.submissionId || '').localeCompare(b.submissionId || ''),
  },
  {
    key: 'name',
    label: 'Name',
    render: (_, r) => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <span className="job-cell-link job-cell-value" style={{ textTransform: 'uppercase' }}>{nameValue(r)}</span>
        </span>
      </span>
    ),
    sorter: (a, b) => nameValue(a).localeCompare(nameValue(b)),
  },
  {
    key: 'designation',
    label: 'Designation',
    dataIndex: 'designation',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v || '—'}</Text>
        </span>
      </span>
    ),
    sorter: (a, b) => (a.designation || '').localeCompare(b.designation || ''),
  },
  {
    key: 'job',
    label: 'Job',
    render: (_, r) => {
      const label = jobCell(r);
      return (
        <span className="job-cell-content">
          <span className="job-cell-main-line">
            {label !== '—'
              ? <span className="job-cell-link job-cell-value">{label}</span>
              : <Text className="job-cell-primary job-cell-value">—</Text>}
          </span>
        </span>
      );
    },
  },
  {
    key: 'payRate',
    label: 'Pay Rate',
    render: (_, r) => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{payRateCell(r)}</Text>
        </span>
      </span>
    ),
    sorter: (a, b) =>
      (a.candidateRate?.candidateAcceptedRate ?? 0) - (b.candidateRate?.candidateAcceptedRate ?? 0),
  },
  {
    key: 'submittedBy',
    label: 'Submitted By',
    dataIndex: 'recruitingUserName',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v || '—'}</Text>
        </span>
      </span>
    ),
    sorter: (a, b) => (a.recruitingUserName || '').localeCompare(b.recruitingUserName || ''),
  },
  {
    key: 'internalStatus',
    label: 'Internal Status',
    render: (_, r) => {
      const text = r.internalSubmissionStatus?.internalSubmissionStatusText;
      return (
        <span className="job-cell-content">
          <span className="job-cell-main-line">
            {text
              ? <Tag color={statusColor(text)} className="job-cell-badge job-cell-value">{text}</Tag>
              : <Text className="job-cell-primary job-cell-value">—</Text>}
          </span>
        </span>
      );
    },
  },
  {
    key: 'clientStatus',
    label: 'Client Status',
    render: (_, r) => {
      const text = r.clientSubmissionStatus?.clientSubmissionStatusText;
      return (
        <span className="job-cell-content">
          <span className="job-cell-main-line">
            {text
              ? <Tag color={statusColor(text)} className="job-cell-badge job-cell-value">{text}</Tag>
              : <Text className="job-cell-primary job-cell-value">—</Text>}
          </span>
        </span>
      );
    },
  },
];

export default function SubmittedTab({ jobId, onCountChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading]             = useState(false);
  const [allData, setAllData]             = useState([]);
  const [page, setPage]                   = useState(1);
  const [pageSize, setPageSize]           = useState(DEFAULT_PAGE_SIZE);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows]   = useState([]);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(ALL_COLUMNS.map(c => c.key));
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const menuItems = SUBMITTED_ACTIONS.map((action) => ({
    key: action.key,
    label: action.label,
    danger: action.danger ?? false,
    onClick: () => {
      navigate(action.route, {
        state: {
          background: location,
          selectedIds: selectedRowKeys,
          selectedRecords: selectedRows,
          moduleName: 'candidate',
          ...(action.popupState ?? {}),
        },
      });
    },
  }));

  const fetchData = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const list = await getSubmittedCandidates(jobId);
      const arr  = Array.isArray(list) ? list : [];
      setAllData(arr);
      onCountChange?.(arr.length);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [jobId]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return allData.slice(start, start + pageSize);
  }, [allData, page, pageSize]);

  const visibleColumns = ALL_COLUMNS.filter(c => visibleColumnKeys.includes(c.key));
  const allVisible     = visibleColumnKeys.length === ALL_COLUMNS.length;
  const someVisible    = visibleColumnKeys.length > 0 && !allVisible;

  const toggleColumn = (key, checked) => {
    setVisibleColumnKeys(prev => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      if (prev.length <= MIN_VISIBLE) return prev;
      return prev.filter(k => k !== key);
    });
  };

  const columnVisibilityContent = (
    <div className="column-visibility-menu" onClick={e => e.stopPropagation()}>
      <Checkbox
        checked={allVisible}
        indeterminate={someVisible}
        onChange={e => setVisibleColumnKeys(
          e.target.checked ? ALL_COLUMNS.map(c => c.key) : ALL_COLUMNS.slice(0, MIN_VISIBLE).map(c => c.key)
        )}
      >
        Select All
      </Checkbox>
      {ALL_COLUMNS.map(col => (
        <Checkbox
          key={col.key}
          checked={visibleColumnKeys.includes(col.key)}
          disabled={visibleColumnKeys.includes(col.key) && visibleColumnKeys.length <= MIN_VISIBLE}
          onChange={e => toggleColumn(col.key, e.target.checked)}
        >
          {col.label}
        </Checkbox>
      ))}
    </div>
  );

  return (
    <div className="antd dynamic-list-view">
      <Card>
        <Flex align="center" gap={3}>
          <Dropdown
            open={columnMenuOpen}
            onOpenChange={setColumnMenuOpen}
            trigger={['click']}
            dropdownRender={() => columnVisibilityContent}
            placement="bottomLeft"
            overlayClassName="column-visibility-dropdown"
          >
            <Button
              aria-label="Display columns"
              type="text"
              icon={<img src={unorderedListOutlinedIcon} alt="" className="job-action-list-icon" />}
              size="small"
              className="job-actions-button job-column-display-button"
            >
              <DownOutlined className="job-actions-caret" />
            </Button>
          </Dropdown>
          {selectedRowKeys.length > 0 && (
            <>
              <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                <Button type="text" size="small" className="job-actions-button job-actions-menu-button">
                  Actions <DownOutlined className="job-actions-caret" />
                </Button>
              </Dropdown>
              <Text className="job-selected-count">
                Selected ({selectedRowKeys.length})
              </Text>
            </>
          )}
        </Flex>
      </Card>

      <Spin spinning={loading}>
        <Table
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys, rows) => { setSelectedRowKeys(keys); setSelectedRows(rows); },
          }}
          columns={visibleColumns}
          dataSource={pagedData}
          size="middle"
          showSorterTooltip={false}
          tableLayout="fixed"
          pagination={false}
          className="job-list-table"
          rowKey={r => r._id || r.submissionId || Math.random()}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" /> }}
        />
      </Spin>

      <CustomPagination
        current={page}
        pageSize={pageSize}
        total={allData.length}
        onChange={p => setPage(p)}
        onPageSizeChange={size => { setPageSize(size); setPage(1); }}
      />
    </div>
  );
}
